import React from 'react';
import Header from '../components/common/Header';
import Footer from '../components/Footer/Footer';
import { Link } from 'react-router-dom';

const NOTE_BOX = { background: '#fff3e5', color: 'black', padding: '10px' };
const ACCENT = { color: '#df8525' };
const CONTACT_EMAIL = 'info@futurefamilyai.com';

const MAIL_SUBJECT = 'Account deletion request - Future Family';
const MAIL_BODY = [
  'Hello Future Family team,',
  '',
  'Please permanently delete my Future Family account and the personal data linked to it.',
  '',
  'Full name:',
  'Registered email address:',
  'Registered phone number:',
  '',
  'I understand that this cannot be undone.',
].join('\n');
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(MAIL_SUBJECT)}&body=${encodeURIComponent(MAIL_BODY)}`;

const BUTTON = {
  display: 'inline-block',
  background: '#df8525',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: 8,
  fontWeight: 600,
  textDecoration: 'none',
};

const STEP_NUMBER = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: '#df8525',
  color: '#fff',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const AccountDeletion = () => {
  return (
    <>
      <Header />
      <div className="policy-term-page">
        <div className="heading-condition">
          <div className="container">
            <h1 className="display-4 fw-bold mb-3 text-center">Delete Your Account</h1>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb justify-content-center">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Delete Account
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-12 col-xl-12">
              <div className="mb-2">
                <div className="privacy-part">
                  You can ask us to delete your Future Family account and personal data at any time.
                </div>
              </div>

              {/* Warning */}
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <h3 className="h4 text-primary1 mb-3">Before You Delete</h3>
                  <div className="alert" style={NOTE_BOX}>
                    <strong>Deleting your account is permanent.</strong> Your profile, health data and reports
                    cannot be recovered once the deletion is complete.
                  </div>
                  <ul className="mb-0">
                    <li>Save any reports or documents you want to keep, because they will be removed</li>
                    <li>
                      Cancel your upcoming appointments from the <strong>Appointments</strong> page so the doctor's
                      time slot is freed
                    </li>
                    <li>Disconnect any wearable or health app you connected</li>
                  </ul>
                </div>
              </div>

              {/* How to request */}
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <h3 className="h4 text-primary1 mb-3">How to Request Deletion</h3>

                  <div className="d-flex mb-3">
                    <div style={STEP_NUMBER}>1</div>
                    <div className="ms-3">
                      <strong>Send us your request</strong>
                      <p className="mb-0 small1">
                        Email us from the address registered on your account, or use the Support section inside
                        the app.
                      </p>
                    </div>
                  </div>
                  <div className="d-flex mb-3">
                    <div style={STEP_NUMBER}>2</div>
                    <div className="ms-3">
                      <strong>We confirm it is you</strong>
                      <p className="mb-0 small1">
                        We may reply to check your identity, so your account is only deleted at your request.
                      </p>
                    </div>
                  </div>
                  <div className="d-flex mb-4">
                    <div style={STEP_NUMBER}>3</div>
                    <div className="ms-3">
                      <strong>Your account is deleted</strong>
                      <p className="mb-0 small1">
                        We remove your personal data within 30 days and send you a confirmation.
                      </p>
                    </div>
                  </div>

                  <a href={MAILTO} style={BUTTON}>
                    Request account deletion
                  </a>
                  <p className="mt-3 mb-0 small1">
                    This opens your email app with a ready-made message to{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} style={ACCENT}>{CONTACT_EMAIL}</a>.
                  </p>
                </div>
              </div>

              {/* What is deleted / kept */}
              <div className="card shadow-sm mb-4">
                <div className="card-body">
                  <h3 className="h4 text-primary1 mb-3">What Happens to Your Data</h3>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="border rounded p-3 h-100">
                        <h6 className="mb-2">
                          <i className="bi bi-trash-fill text-success me-2"></i>Deleted
                        </h6>
                        <ul className="mb-0">
                          <li>Your account, name, email and phone number</li>
                          <li>Profile and family-planning information</li>
                          <li>Health readings from wearables and health apps</li>
                          <li>Genetic reports and documents you uploaded</li>
                          <li>Reminders and support messages</li>
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="border rounded p-3 h-100">
                        <h6 className="mb-2">
                          <i className="bi bi-archive-fill text-primary1 me-2"></i>May be kept
                        </h6>
                        <ul className="mb-0">
                          <li>
                            Records of past consultations, where a doctor is legally required to keep medical
                            records
                          </li>
                          <li>Payment and invoice records, where tax or accounting law requires</li>
                          <li>Security and fraud-prevention logs, kept for a limited time</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <p className="mb-0">
                    Anything we must keep is stored securely, used only for the legal purpose, and deleted once the
                    required period ends. See our <Link to="/privacy-policy" style={ACCENT}>Privacy Policy</Link>{' '}
                    for more.
                  </p>
                </div>
              </div>

              {/* Only some data */}
              <div className="privacy-part mt-4">
                <h5 className="alert-heading">Want to remove only some data?</h5>
                <p className="mb-0">
                  You do not have to delete everything. You can disconnect a wearable, remove uploaded documents,
                  or edit your details from your profile. For anything else, contact us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} style={ACCENT}>{CONTACT_EMAIL}</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AccountDeletion;